import React, { useCallback, useEffect, useState } from "react";
import api from "../api/client";
import { useCustomer } from "../context/CustomerContext";
import { useSort, sortRows, SortableTh } from "../components/SortableTh";
import AdmxEditor from "../components/AdmxEditor";
import IdentityAssistPanel from "../components/IdentityAssistPanel";

interface Gpo {
  id: string;
  name?: string;
  status?: string;
  wmi_filter?: string | null;
  created?: string;
  modified?: string;
}
interface Link {
  som_path: string;
  dn?: string;
  enabled?: boolean;
  enforced?: boolean;
}
interface Ou {
  name: string;
  dn: string;
}

const CARD =
  "bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm";
const INPUT =
  "border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 rounded-lg px-2 py-1.5 text-sm";
const STATUSES = [
  ["AllSettingsEnabled", "All settings enabled"],
  ["UserSettingsDisabled", "User settings disabled"],
  ["ComputerSettingsDisabled", "Computer settings disabled"],
  ["AllSettingsDisabled", "All settings disabled"],
] as const;

function errText(e: unknown, f: string) {
  const err = e as { response?: { data?: { detail?: string } } };
  return err.response?.data?.detail || f;
}
const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

export default function ToolsGpo() {
  const { currentCustomerId, current } = useCustomer();
  const [gpos, setGpos] = useState<Gpo[] | null>(null);
  const [sel, setSel] = useState<Gpo | null>(null);
  const [links, setLinks] = useState<Link[] | null>(null);
  const [ous, setOus] = useState<Ou[]>([]);
  const [addOu, setAddOu] = useState("");
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [reportHtml, setReportHtml] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [newName, setNewName] = useState("");
  const [renameVal, setRenameVal] = useState("");
  const [copiedGuid, setCopiedGuid] = useState("");
  const [reg, setReg] = useState({ key: "", vname: "", vtype: "DWord", value: "" });
  const gpoSort = useSort("name");

  const runCommand = useCallback(
    async (start: () => Promise<{ data: { command_id: number } }>): Promise<any> => {
      const cid = currentCustomerId;
      const r = await start();
      const id = r.data.command_id;
      // Each GPO operation is a queued round-trip to the AD runner:
      // measured at 8-25s for create/link/refresh, longer for a full
      // settings report. 60s left no headroom and surfaced as
      // "Timed out waiting for the AD runner" on a healthy domain.
      const deadline = Date.now() + 180000;
      while (Date.now() < deadline) {
        await sleep(2500);
        const p = await api.get(`/api/v1/gpo/${cid}/commands/${id}`);
        if (p.data.status === "completed") return p.data.result || {};
        if (p.data.status === "failed" || p.data.status === "expired")
          throw new Error(p.data.error_message || `Command ${p.data.status}`);
      }
      throw new Error("Timed out waiting for the AD runner (is it online?).");
    },
    [currentCustomerId],
  );

  const loadGpos = useCallback(async () => {
    if (currentCustomerId == null) return;
    setBusy("gpos");
    setError("");
    setGpos(null);
    setSel(null);
    setLinks(null);
    try {
      const res = await runCommand(() => api.post(`/api/v1/gpo/${currentCustomerId}/refresh`));
      setGpos(res.gpos || []);
    } catch (e) {
      setError(errText(e, (e as Error).message || "Couldn't load GPOs"));
    } finally {
      setBusy("");
    }
  }, [currentCustomerId, runCommand]);

  const openGpo = async (g: Gpo) => {
    setSel(g);
    setLinks(null);
    setReportHtml("");
    setRenameVal(g.name || "");
    setBusy("links");
    setError("");
    try {
      // Issue links + OUs together so the runner can pick them up in the same
      // poll cycle instead of waiting a fresh poll interval for the second one.
      const linksP = runCommand(() =>
        api.post(`/api/v1/gpo/${currentCustomerId}/links/refresh`, { guid: g.id }),
      );
      const ousP =
        ous.length === 0
          ? runCommand(() => api.post(`/api/v1/gpo/${currentCustomerId}/ous/refresh`))
          : null;
      // The OU list is settled INDEPENDENTLY of the links call. Awaiting
      // links first meant that one slow or failed links refresh threw before
      // setOus ever ran, so the "Link to an OU..." dropdown stayed empty for
      // the rest of the session and the GPO could not be linked at all -
      // reported as "could not link to an OU".
      if (ousP) {
        ousP
          .then((o: { ous?: Ou[] }) => setOus(o.ous || []))
          .catch(() => {
            /* leave the list as-is; the Refresh OUs button retries */
          });
      }
      const res = await linksP;
      setLinks(res.links || []);
      // Load the full settings report inline so the GPO's settings are visible.
      setBusy("report");
      const rep = await runCommand(() => api.post(`/api/v1/gpo/${currentCustomerId}/report`, { guid: g.id }));
      setReportHtml(rep.html || "");
    } catch (e) {
      setError(errText(e, (e as Error).message || "Couldn't load GPO"));
    } finally {
      setBusy("");
    }
  };

  const createGpo = () => {
    const name = newName.trim();
    return act(
      () => api.post(`/api/v1/gpo/${currentCustomerId}`, { name }),
      (res) => {
        setShowNew(false);
        setNewName("");
        // The agent returns the new GUID, so insert it rather than
        // re-listing. A re-list is another queued round-trip, and the new
        // GPO would not appear until it came back.
        const id = res?.id;
        if (id) {
          setGpos((prev) => {
            const next = [...(prev || []), { id, name, status: "AllSettingsEnabled" } as Gpo];
            return next.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
          });
          setBusy("");
        } else {
          // No guid came back - fall back rather than showing nothing.
          loadGpos();
        }
      },
    );
  };

  const deleteGpo = (g: Gpo) =>
    window.confirm(`Delete GPO "${g.name}"? This cannot be undone.`) &&
    act(
      () => api.post(`/api/v1/gpo/${currentCustomerId}/delete`, { guid: g.id }),
      () => {
        // Drop it locally: it is gone, and re-listing to confirm that costs
        // the operator another wait.
        setGpos((prev) => (prev || []).filter((x) => x.id !== g.id));
        setSel(null);
        setBusy("");
      },
    );

  const renameGpo = () => {
    if (!sel || !renameVal.trim() || renameVal.trim() === sel.name) return;
    const guid = sel.id;
    const newName2 = renameVal.trim();
    // We know the new name; no need to ask the domain what it is.
    act(
      () => api.post(`/api/v1/gpo/${currentCustomerId}/rename`, { guid, new_name: newName2 }),
      () => {
        setGpos((prev) =>
          (prev || [])
            .map((g) => (g.id === guid ? { ...g, name: newName2 } : g))
            .sort((a, b) => (a.name || "").localeCompare(b.name || "")),
        );
        setSel((s) => (s && s.id === guid ? { ...s, name: newName2 } : s));
        setBusy("");
      },
    );
  };

  const setRegistry = () => {
    if (!sel || !reg.key.trim() || !reg.vname.trim()) return;
    act(
      () =>
        api.post(`/api/v1/gpo/${currentCustomerId}/registry`, {
          guid: sel.id,
          key: reg.key.trim(),
          value_name: reg.vname.trim(),
          value_type: reg.vtype,
          value: reg.value,
        }),
      () => {
        setReg({ key: "", vname: "", vtype: "DWord", value: "" });
        openGpo(sel); // refresh the inline report so the new setting shows
      },
    );
  };

  // `after` gets the command RESULT, so a caller that already knows what
  // changed can update the list in place instead of paying a second
  // round-trip to the AD runner. Every one of those is a queued agent
  // command, and creating a GPO used to pay the toll twice - once to
  // create, once for a full re-list - which is most of why the screen felt
  // like it was "not pulling in refreshed data quickly".
  const act = async (start: () => Promise<any>, after: (result?: any) => void) => {
    setBusy("act");
    setError("");
    try {
      const res = await runCommand(start);
      after(res);
    } catch (e) {
      setError(errText(e, (e as Error).message || "Action failed"));
      setBusy("");
    }
  };

  const viewReport = async (g: Gpo) => {
    setBusy("report");
    setError("");
    try {
      const res = await runCommand(() =>
        api.post(`/api/v1/gpo/${currentCustomerId}/report`, { guid: g.id }),
      );
      const html = res.html || "<p>No report.</p>";
      const w = window.open("", "_blank");
      if (w) {
        w.document.open();
        w.document.write(html);
        w.document.close();
      }
    } catch (e) {
      setError(errText(e, (e as Error).message || "Couldn't get report"));
    } finally {
      setBusy("");
    }
  };

  useEffect(() => {
    setGpos(null);
    setSel(null);
    setLinks(null);
    setOus([]);
    setError("");
  }, [currentCustomerId]);

  if (currentCustomerId == null) {
    return (
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-1">Group Policy</h1>
        <p className="text-sm text-gray-400 dark:text-gray-500 italic mt-4">
          Select a customer in the header to manage its Group Policy.
        </p>
      </div>
    );
  }

  const sorted = sortRows(gpos || [], gpoSort.sort, (g, k) => String((g as any)[k] ?? "").toLowerCase());

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-1">Group Policy</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            GPOs on {current?.name}'s domain, via its AD runner.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {gpos && (
            <button
              onClick={() => setShowNew((v) => !v)}
              disabled={!!busy}
              className="text-sm border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 px-3 py-1.5 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50"
            >
              + New GPO
            </button>
          )}
          <button
            onClick={loadGpos}
            disabled={!!busy}
            className="text-sm bg-blue-600 text-white px-4 py-1.5 rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            {busy === "gpos" ? "Loading…" : gpos ? "Reload GPOs" : "Load GPOs"}
          </button>
        </div>
      </div>

      <IdentityAssistPanel customerId={currentCustomerId} scope="gpo" />

      {showNew && (
        <div className={`${CARD} p-4 mb-3 flex flex-wrap items-end gap-2`}>
          <input
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="New GPO name"
            className={`${INPUT} w-64`}
          />
          <button
            onClick={createGpo}
            disabled={!!busy || !newName.trim()}
            className="text-sm bg-blue-600 text-white px-3 py-1.5 rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            Create GPO
          </button>
          <span className="text-xs text-gray-400 dark:text-gray-500">
            Creates an unlinked GPO — link it to an OU from its detail panel.
          </span>
        </div>
      )}

      {error && (
        <p className="text-xs text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 px-3 py-2 rounded-lg mb-3">
          {error}
        </p>
      )}
      {busy && !error && (
        <div className="mb-3 flex items-center gap-3 rounded-lg border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20 px-4 py-3">
          <span className="h-4 w-4 shrink-0 rounded-full border-2 border-blue-500 border-t-transparent animate-spin" />
          <div>
            <div className="text-sm font-medium text-blue-800 dark:text-blue-200">Working…</div>
            <div className="text-xs text-blue-700 dark:text-blue-300">
              Waiting for {current?.name}'s on-site runner — usually a few seconds, up to ~20s.
            </div>
          </div>
        </div>
      )}

      {gpos && (
        <div className="grid gap-4 lg:grid-cols-[1fr,1fr]">
          {/* GPO list */}
          <div className={`${CARD} overflow-x-auto self-start`}>
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-900/50 border-b dark:border-gray-700">
                <tr>
                  <SortableTh label="Name" sortKey="name" sort={gpoSort.sort} onSort={gpoSort.toggle} />
                  <SortableTh label="Status" sortKey="status" sort={gpoSort.sort} onSort={gpoSort.toggle} />
                  <SortableTh label="GUID" sortKey="id" sort={gpoSort.sort} onSort={gpoSort.toggle} />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {sorted.map((g) => (
                  <tr
                    key={g.id}
                    onClick={() => openGpo(g)}
                    className={`cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/40 ${
                      sel?.id === g.id ? "bg-blue-50 dark:bg-blue-900/20" : ""
                    }`}
                  >
                    <td className="px-3 py-1.5 text-gray-900 dark:text-gray-100">{g.name}</td>
                    <td className="px-3 py-1.5 text-gray-600 dark:text-gray-300 text-xs">{g.status}</td>
                    <td className="px-3 py-1.5 text-xs">
                      <span className="inline-flex items-center gap-1.5">
                        <code className="font-mono text-gray-500 dark:text-gray-400 select-all">{g.id}</code>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            navigator.clipboard?.writeText(g.id);
                            setCopiedGuid(g.id);
                            setTimeout(() => setCopiedGuid((c) => (c === g.id ? "" : c)), 1500);
                          }}
                          title="Copy GUID"
                          className="text-gray-400 hover:text-blue-600 dark:hover:text-blue-400"
                        >
                          {copiedGuid === g.id ? "✓" : "⧉"}
                        </button>
                      </span>
                    </td>
                  </tr>
                ))}
                {gpos.length === 0 && (
                  <tr>
                    <td colSpan={3} className="px-3 py-6 text-center text-gray-400 dark:text-gray-500">No GPOs.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* GPO detail */}
          {sel && (
            <div className={`${CARD} p-4 self-start`}>
              <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                <div className="flex items-center gap-1.5 flex-1 min-w-0">
                  <input
                    value={renameVal}
                    onChange={(e) => setRenameVal(e.target.value)}
                    className={`${INPUT} flex-1 min-w-0 font-semibold`}
                  />
                  <button
                    onClick={renameGpo}
                    disabled={!!busy || !renameVal.trim() || renameVal.trim() === sel.name}
                    className="text-xs text-blue-600 dark:text-blue-400 hover:underline disabled:opacity-40 disabled:no-underline"
                  >
                    Rename
                  </button>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button
                    onClick={() => viewReport(sel)}
                    disabled={!!busy}
                    className="text-xs bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 px-3 py-1 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50"
                  >
                    Open report ↗
                  </button>
                  <button
                    onClick={() => deleteGpo(sel)}
                    disabled={!!busy}
                    className="text-xs text-red-600 dark:text-red-400 hover:underline disabled:opacity-50"
                  >
                    Delete
                  </button>
                </div>
              </div>

              {/* GUID — the AI assistant references a GPO by this */}
              <div className="flex items-center gap-1.5 mb-3 text-xs">
                <span className="text-gray-400 dark:text-gray-500 uppercase">GUID</span>
                <code className="font-mono text-gray-500 dark:text-gray-400 select-all break-all">{sel.id}</code>
                <button
                  onClick={() => {
                    navigator.clipboard?.writeText(sel.id);
                    setCopiedGuid(sel.id);
                    setTimeout(() => setCopiedGuid((c) => (c === sel.id ? "" : c)), 1500);
                  }}
                  title="Copy GUID"
                  className="text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 shrink-0"
                >
                  {copiedGuid === sel.id ? "✓ copied" : "⧉ copy"}
                </button>
              </div>

              {/* Status */}
              <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200 mb-4">
                Status
                <select
                  value={sel.status}
                  onChange={(e) =>
                    act(
                      () => api.post(`/api/v1/gpo/${currentCustomerId}/status`, { guid: sel.id, status: e.target.value }),
                      () => loadGpos(),
                    )
                  }
                  disabled={!!busy}
                  className={INPUT}
                >
                  {STATUSES.map(([v, l]) => (
                    <option key={v} value={v}>
                      {l}
                    </option>
                  ))}
                </select>
              </label>

              {/* Links */}
              <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mb-1">
                Links
              </div>
              <div className="space-y-1 mb-3">
                {links === null && <p className="text-sm text-gray-400 dark:text-gray-500">Loading links…</p>}
                {links && links.length === 0 && (
                  <p className="text-sm text-gray-400 dark:text-gray-500">Not linked anywhere.</p>
                )}
                {(links || []).map((lk, i) => (
                  <div key={i} className="flex items-center justify-between gap-2 text-sm py-0.5">
                    <span className="text-gray-700 dark:text-gray-200 break-all">
                      {lk.som_path}
                      {lk.enforced && (
                        <span className="ml-2 text-[10px] bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300 px-1.5 py-0.5 rounded-full">enforced</span>
                      )}
                    </span>
                    <span className="flex items-center gap-2 shrink-0">
                      <button
                        onClick={() =>
                          act(
                            () => api.post(`/api/v1/gpo/${currentCustomerId}/links/update`, { guid: sel.id, target_ou: lk.dn, enabled: !lk.enabled }),
                            () => openGpo(sel),
                          )
                        }
                        disabled={!!busy || !lk.dn}
                        className={`text-xs px-2 py-0.5 rounded-full ${
                          lk.enabled
                            ? "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300"
                            : "bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400"
                        } disabled:opacity-50`}
                        title="Toggle link enabled"
                      >
                        {lk.enabled ? "enabled" : "disabled"}
                      </button>
                      <button
                        onClick={() =>
                          window.confirm(`Unlink from ${lk.som_path}?`) &&
                          act(
                            () => api.post(`/api/v1/gpo/${currentCustomerId}/links/delete`, { guid: sel.id, target_ou: lk.dn }),
                            () => openGpo(sel),
                          )
                        }
                        disabled={!!busy || !lk.dn}
                        className="text-xs text-red-600 dark:text-red-400 hover:underline disabled:opacity-50"
                      >
                        Remove
                      </button>
                    </span>
                  </div>
                ))}
              </div>

              {/* Add link */}
              <div className="flex items-center gap-2">
                <select value={addOu} onChange={(e) => setAddOu(e.target.value)} className={`${INPUT} flex-1`}>
                  <option value="">Link to an OU…</option>
                  {ous.map((o) => (
                    <option key={o.dn} value={o.dn}>
                      {o.name}
                    </option>
                  ))}
                </select>
                <button
                  onClick={() =>
                    act(
                      () => api.post(`/api/v1/gpo/${currentCustomerId}/links`, { guid: sel.id, target_ou: addOu, enabled: true }),
                      () => {
                        setAddOu("");
                        openGpo(sel);
                      },
                    )
                  }
                  disabled={!!busy || !addOu}
                  className="text-sm bg-blue-600 text-white px-3 py-1.5 rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  Link
                </button>
              </div>

              {/* Administrative Templates (GPMC-style) editor */}
              <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mt-5 mb-2">
                Administrative Templates
              </div>
              <AdmxEditor customerId={currentCustomerId} gpoGuid={sel.id} gpoName={sel.name || sel.id} />

              {/* Advanced: raw registry setting editor (escape hatch) */}
              <details className="mt-5">
                <summary className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase cursor-pointer">
                  Advanced: set a raw registry value
                </summary>
                <div className="mt-2">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-2">
                <input
                  value={reg.key}
                  onChange={(e) => setReg((r) => ({ ...r, key: e.target.value }))}
                  placeholder="Key (e.g. HKLM\Software\Policies\...)"
                  className={`${INPUT} w-full sm:col-span-2`}
                />
                <input
                  value={reg.vname}
                  onChange={(e) => setReg((r) => ({ ...r, vname: e.target.value }))}
                  placeholder="Value name"
                  className={`${INPUT} w-full`}
                />
                <select
                  value={reg.vtype}
                  onChange={(e) => setReg((r) => ({ ...r, vtype: e.target.value }))}
                  className={`${INPUT} w-full`}
                >
                  <option value="String">String (REG_SZ)</option>
                  <option value="ExpandString">ExpandString (REG_EXPAND_SZ)</option>
                  <option value="DWord">DWord (REG_DWORD)</option>
                  <option value="QWord">QWord (REG_QWORD)</option>
                </select>
                <input
                  value={reg.value}
                  onChange={(e) => setReg((r) => ({ ...r, value: e.target.value }))}
                  placeholder="Value"
                  className={`${INPUT} w-full sm:col-span-2`}
                />
              </div>
              <button
                onClick={setRegistry}
                disabled={!!busy || !reg.key.trim() || !reg.vname.trim()}
                className="text-sm bg-blue-600 text-white px-3 py-1.5 rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                Apply setting
              </button>
              <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-1">
                The key's hive (HKLM/HKCU) picks whether this lands under Computer or User configuration.
              </p>
                </div>
              </details>

              {/* Inline settings report */}
              <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mt-5 mb-1">
                Settings
              </div>
              {busy === "report" && !reportHtml ? (
                <p className="text-sm text-gray-400 dark:text-gray-500">Loading settings report…</p>
              ) : reportHtml ? (
                <iframe
                  title="GPO settings report"
                  srcDoc={reportHtml}
                  className="w-full h-[520px] rounded-lg border border-gray-200 dark:border-gray-700 bg-white"
                />
              ) : (
                <p className="text-sm text-gray-400 dark:text-gray-500">No settings report available.</p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
